/**
 * MDD (maximum daily dose) helpers for PRN medications.
 *
 * Owner decisions (2026-09-04): every PRN medication records its MDD in the
 * dosage field — the DOSE field relabels to MAX DAILY DOSE. When the frequency
 * carries a schedule ("Every 4-6 hours as needed"), the implied ceiling is
 * computable and offered as a one-tap suggestion; a pure PRN ("As needed")
 * implies nothing, so the nurse states the ceiling herself.
 */

/** A medication is PRN when the frequency says "as needed" (or raw "PRN"). */
export function isPrnFrequency(freq: string | null | undefined): boolean {
    if (!freq) {
        return false;
    }
    return /\bprn\b/i.test(freq) || /as needed/i.test(freq);
}

/**
 * The maximum administrations implied by a normalized frequency in any one
 * 24-hour window, or null when the frequency implies no usable ceiling.
 *
 * Covers the full normalized vocabulary (medication-vocabulary.service.ts
 * SCHEDULES) plus the legacy raw forms still stored on older rows ("QID PRN",
 * "Q4-8H PRN", "Every 4 to 6 hours"). Deliberate nulls:
 *  - "As needed" / bare "PRN"      — pure PRN, nothing implied
 *  - "Every {n} minutes"           — rescue sigs; 1440/n is a number no nurse
 *                                    should be nudged toward (every 15 min ⇒ 96)
 *  - "With meals and snacks"       — no fixed count by design
 *  - meal-anchored times ("Before meals") — count depends on meals eaten
 *  - event triggers ("After each loose stool") — unbounded by the clock
 *  - Continuous / Stopped / Not specified
 */
export function suggestedMaxPerDay(freq: string | null | undefined): number | null {
    if (!freq) {
        return null;
    }
    const f = freq.trim();

    // "{n}-{m} times daily" and "N times daily|a day" (a range takes the max);
    // legacy rows also spell the count out ("Three times a day").
    const words: Record<string, number> = { one: 1, two: 2, three: 3, four: 4, five: 5, six: 6 };
    const timesDaily = f.match(
        /(\d+|one|two|three|four|five|six)(?:\s*(?:-|–|to)\s*(\d+))?\s*times\s*(?:a\s*day|daily)/i
    );
    if (timesDaily) {
        const hi = timesDaily[2] ?? timesDaily[1]!;
        return words[hi.toLowerCase()] ?? Number(hi);
    }
    // Word-count ladder + abbreviations. Twice/TID/QID must run BEFORE the bare
    // "daily" fallback — "Twice daily" contains "daily".
    if (/\btwice daily\b|\bbid\b/i.test(f)) {
        return 2;
    }
    if (/\btid\b/i.test(f)) {
        return 3;
    }
    if (/\bqid\b/i.test(f)) {
        return 4;
    }
    if (/\bonce daily\b|\bdaily\b|\bnightly\b|\bqd\b|\bqhs\b/i.test(f)) {
        return 1;
    }
    // "Hourly"
    if (/\bhourly\b/i.test(f)) {
        return 24;
    }
    // "Every {n}(-{m}| to {m}) hours" / "Q4-8H" — shortest interval bounds the day
    const everyHours = f.match(
        /(?:every|q)\s*(\d+)(?:\s*(?:-|–|to)\s*\d+)?(?:\s*\([a-z]+\))?\s*h(?:ours?)?\b/i
    );
    if (everyHours) {
        return Math.max(1, Math.floor(24 / Number(everyHours[1])));
    }
    // "Every {n} minutes" — computable but deliberately NOT suggested
    if (/(?:every|q)\s*\d+(?:\s*(?:-|–|to)\s*\d+)?\s*min/i.test(f)) {
        return null;
    }
    // Every other day / every N days / weekly / monthly families: at most one
    // administration falls in any single 24-hour window.
    if (
        /\bevery other day\b|\bevery \d+ days?\b|\bweekly\b|\bevery \d+ weeks?\b|\bmonthly\b|\bevery \d+ months?\b|\bone time only\b|\bonce\b/i.test(
            f
        )
    ) {
        return 1;
    }
    // "With meals" = 3; "with meals and snacks" has no fixed count
    if (/with meals and snacks/i.test(f)) {
        return null;
    }
    if (/with (?:each )?meals?\b/i.test(f)) {
        return 3;
    }
    // Anchor displays: count single-point anchors ("At bedtime", "In the
    // morning and at bedtime"). Any meal-based anchor makes the count depend
    // on meals eaten → no suggestion.
    const anchorable = f
        .replace(/\s*(?:as needed|prn).*$/i, "")
        .split(/\s*(?:,|\band\b)\s*/i)
        .filter(Boolean);
    if (
        anchorable.length > 0 &&
        anchorable.every((a) =>
            /^(at bedtime|in the morning|in the evening|at noon|in the afternoon|upon waking)$/i.test(
                a.trim()
            )
        )
    ) {
        return anchorable.length;
    }
    return null;
}

/** First number in an ordered dose: "2 tablets" -> 2. */
export function doseAmount(orderedDose: string | null | undefined): number | null {
    const m = (orderedDose ?? "").match(/(\d+(?:\.\d+)?)/);
    return m ? Number(m[1]) : null;
}

/** Unit of an ordered dose: "2 tablets" -> "tablets". */
export function doseUnit(orderedDose: string | null | undefined): string {
    const m = (orderedDose ?? "").match(/\d+(?:\.\d+)?\s*(.+)$/);
    return m ? m[1]!.trim() : "doses";
}

const PLURALIZABLE_UNITS =
    /^(tablet|capsule|tab|cap|puff|spray|drop|patch|suppository|lozenge|piece|dose)$/i;

/** "tablet" -> "tablets" when count != 1; measured units (mg, mL) never change. */
export function pluralizeUnit(unit: string, count: number): string {
    if (count !== 1 && PLURALIZABLE_UNITS.test(unit)) {
        return `${unit}s`;
    }
    if (count === 1 && /s$/i.test(unit) && PLURALIZABLE_UNITS.test(unit.replace(/s$/i, ""))) {
        return unit.replace(/s$/i, "");
    }
    return unit;
}

/**
 * The one-tap MDD suggestion for a scheduled PRN: per-dose amount × the
 * schedule's implied max/day. Null when either half is missing — then the
 * nurse enters the ceiling manually.
 */
export function computeSuggestedMdd(
    perDose: string | null | undefined,
    freq: string | null | undefined
): { value: string; explain: string } | null {
    const perDay = suggestedMaxPerDay(freq);
    const amount = doseAmount(perDose);
    if (perDay == null || amount == null) {
        return null;
    }
    const total = amount * perDay;
    // A fractional total ("1.5 tablets") is fine; a zero is not.
    if (!(total > 0)) {
        return null;
    }
    const unit = pluralizeUnit(doseUnit(perDose), total);
    return {
        value: `${total} ${unit}`,
        explain: `${amount} ${pluralizeUnit(doseUnit(perDose), amount)} × ${perDay}/day`
    };
}

export interface MddSuggestion {
    value: string;
    explain: string;
}

/**
 * The chips a card offers for the MDD. When the schedule implies a ceiling,
 * that single derived value. When it doesn't (pure PRN), three candidates
 * built from the per-dose amount at the common PRN cadences — 3, 4 and 6
 * times a day — each labelled with its arithmetic so the nurse sees where
 * every number came from. Nothing when even the per-dose amount is unknown.
 */
export function mddChipSuggestions(
    perDose: string | null | undefined,
    freq: string | null | undefined
): MddSuggestion[] {
    const calc = computeSuggestedMdd(perDose, freq);
    if (calc) {
        return [calc];
    }
    const amount = doseAmount(perDose);
    if (amount == null) {
        return [];
    }
    const unit = doseUnit(perDose);
    return [3, 4, 6].map((n) => ({
        value: `${amount * n} ${pluralizeUnit(unit, amount * n)}`,
        explain: `${amount} ${pluralizeUnit(unit, amount)} × ${n}/day`
    }));
}
